import { useState, useRef, useEffect } from 'react'
import { whisperCreate } from '../../../../../examination-utils/examination-lib'
import { assistantInitiate } from '../../../../../../AI Tools/utils'
import { generateAIResponse } from '../../../../../../AI Tools/assistants-lib'
import { AssistantEnums } from '../../../../../../AI Tools/assitants-enums'
import { ERROR_MESSAGES, MAX_RECORDING_TIME } from '../../../utils/constants'
import { getWorkspaceData, updateWorkspaceData } from '../utils/utils'
import { DEFAULT_LANGUAGE, getLanguageName, WHISPER_LANGUAGES } from '../utils/languageConfig'
import { message } from 'antd'

const useSimplifiedTranscribe = ({ setNoteState, noteState, debouncedNoteCapture, bookingData }) => {
  const [persistedData, setPersistedData] = useState({
    generatedNote: '',
    rawAiResponse: '',
    originalTranscript: ''
  })

  useEffect(() => {
    const handleGetWorkspaceData = async (bookingRef) => {
      try {
        const workspaceData = await getWorkspaceData({ bookingRef: bookingRef })
        setPersistedData(workspaceData)
      } catch (error) {
        message.error('Error getting workspace data')
        console.error('Error getting workspace data:', error)
      }
    }

    if (bookingData && bookingData._id) {
      handleGetWorkspaceData(bookingData._id)
    }
  }, [bookingData])

  useEffect(() => {
    setGeneratedNote(persistedData?.generatedNote)
    setRawAiResponse(persistedData?.rawAiResponse)
    setOriginalTranscript(persistedData?.originalTranscript)
  }, [persistedData])

  // Recording state
  const [isRecording, setIsRecording] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const [recordingTime, setRecordingTime] = useState(0)
  const [audioBlob, setAudioBlob] = useState(null)

  // Processing state
  const [isProcessing, setIsProcessing] = useState(false)
  const [isTyping, setIsTyping] = useState(false)
  const [generatedNote, setGeneratedNote] = useState(persistedData?.generatedNote)
  const [rawAiResponse, setRawAiResponse] = useState(persistedData?.rawAiResponse)
  const [originalTranscript, setOriginalTranscript] = useState(persistedData?.originalTranscript) // Store original transcript for regeneration
  const [error, setError] = useState('')

  // Audio analysis state
  const [audioLevels, setAudioLevels] = useState([0, 0, 0, 0, 0])

  // Language state with localStorage persistence
  const [selectedLanguage, setSelectedLanguageState] = useState(() => {
    // Load saved language preference or use default
    const savedLanguage = localStorage.getItem('transcribeai-preferred-language')

    // Validate that the saved language is still supported
    if (savedLanguage) {
      const isValidLanguage = WHISPER_LANGUAGES.some((lang) => lang.code === savedLanguage)
      if (isValidLanguage) {
        return savedLanguage
      } else {
        console.warn(`Saved language "${savedLanguage}" is no longer supported, reverting to default`)
        localStorage.removeItem('transcribeai-preferred-language')
      }
    }

    return DEFAULT_LANGUAGE
  })

  // Custom setter that also saves to localStorage
  const setSelectedLanguage = (language) => {
    setSelectedLanguageState(language)
    localStorage.setItem('transcribeai-preferred-language', language)
    console.log(`Language preference saved: ${getLanguageName(language)} (${language})`)
  }

  // Log loaded language preference on mount
  useEffect(() => {
    const savedLanguage = localStorage.getItem('transcribeai-preferred-language')
    if (savedLanguage) {
      console.log(`Loaded saved language preference: ${getLanguageName(savedLanguage)} (${savedLanguage})`)
    } else {
      console.log(`Using default language: ${getLanguageName(DEFAULT_LANGUAGE)} (${DEFAULT_LANGUAGE})`)
    }
  }, [])

  // Function to clear language preference (useful for testing/reset)
  const clearLanguagePreference = () => {
    localStorage.removeItem('transcribeai-preferred-language')
    setSelectedLanguageState(DEFAULT_LANGUAGE)
    console.log('Language preference cleared, reverted to default')
  }

  // References
  const mediaRecorderRef = useRef(null)
  const audioChunksRef = useRef([])
  const timerRef = useRef(null)
  const audioContextRef = useRef(null)
  const analyzerRef = useRef(null)
  const animationFrameRef = useRef(null)
  const isRecordingRef = useRef(false)
  const isPausedRef = useRef(false)
  const isCleaningUpRef = useRef(false)

  // Effect to persist data when important state changes
  useEffect(() => {
    const handleCreateWorkspaceData = async (data) => {
      try {
        await updateWorkspaceData({ bookingRef: bookingData._id }, data)
      } catch (error) {
        message.error('Error updating workspace data')
        console.error('Error updating workspace data:', error)
      }
    }

    if (generatedNote || rawAiResponse || originalTranscript) {
      handleCreateWorkspaceData({
        bookingRef: bookingData._id,
        generatedNote,
        rawAiResponse,
        originalTranscript
      })
    }
    // eslint-disable-next-line
  }, [generatedNote, rawAiResponse, originalTranscript])

  // Timer for recording - only counts when recording and not paused
  useEffect(() => {
    isRecordingRef.current = isRecording
    isPausedRef.current = isPaused

    if (isRecording && !isPaused) {
      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => {
          const newTime = prev + 1000
          if (newTime >= MAX_RECORDING_TIME) {
            handleStopRecording()
            return MAX_RECORDING_TIME
          }
          return newTime
        })
      }, 1000)
    } else {
      clearInterval(timerRef.current)
    }

    return () => clearInterval(timerRef.current)
  }, [isRecording, isPaused])

  // Audio analysis for wave animation
  const analyzeAudio = (stream) => {
    try {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)()
      analyzerRef.current = audioContextRef.current.createAnalyser()
      const source = audioContextRef.current.createMediaStreamSource(stream)

      analyzerRef.current.fftSize = 512
      analyzerRef.current.smoothingTimeConstant = 0.3
      source.connect(analyzerRef.current)

      const updateLevels = () => {
        if (!analyzerRef.current || !isRecordingRef.current) return

        if (isPausedRef.current) {
          // Show flat levels when paused but keep the animation loop running
          setAudioLevels([0.1, 0.1, 0.1, 0.1, 0.1])
          animationFrameRef.current = requestAnimationFrame(updateLevels)
          return
        }

        const dataArray = new Uint8Array(analyzerRef.current.fftSize)
        analyzerRef.current.getByteTimeDomainData(dataArray)

        // Calculate RMS (Root Mean Square) for overall volume
        let sum = 0
        for (let i = 0; i < dataArray.length; i++) {
          const amplitude = (dataArray[i] - 128) / 128
          sum += amplitude * amplitude
        }
        const rms = Math.sqrt(sum / dataArray.length)

        // Create 5 bars with different sensitivities and slight variations
        const baseLevel = Math.min(1, rms * 15) // Much higher sensitivity
        const newLevels = [
          Math.min(1, baseLevel * (0.8 + Math.random() * 0.4)), // Bar 1: 80-120% of base
          Math.min(1, baseLevel * (1.0 + Math.random() * 0.5)), // Bar 2: 100-150% of base
          Math.min(1, baseLevel * (0.9 + Math.random() * 0.3)), // Bar 3: 90-120% of base
          Math.min(1, baseLevel * (1.1 + Math.random() * 0.6)), // Bar 4: 110-170% of base
          Math.min(1, baseLevel * (0.7 + Math.random() * 0.4)) // Bar 5: 70-110% of base
        ]

        setAudioLevels(newLevels)

        if (isRecordingRef.current) {
          animationFrameRef.current = requestAnimationFrame(updateLevels)
        }
      }

      updateLevels()
    } catch (error) {
      console.error('Error setting up audio analysis:', error)
      // Fallback to default animation if audio analysis fails
      setAudioLevels([0.5, 0.7, 0.4, 0.8, 0.3])
    }
  }

  // Cleanup audio analysis
  const stopAudioAnalysis = () => {
    if (isCleaningUpRef.current) {
      return // Already cleaning up, prevent duplicate calls
    }

    isCleaningUpRef.current = true

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close()
    }
    audioContextRef.current = null
    analyzerRef.current = null
    setAudioLevels([0, 0, 0, 0, 0])

    // Reset cleanup flag after a short delay
    setTimeout(() => {
      isCleaningUpRef.current = false
    }, 100)
  }

  // Start recording function
  const handleStartRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      })

      // Initialize MediaRecorder
      mediaRecorderRef.current = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      })

      audioChunksRef.current = []

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data)
        }
      }

      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' })
        setAudioBlob(audioBlob)
        stream.getTracks().forEach((track) => track.stop())

        // Automatically process the recording
        processRecording(audioBlob)
      }

      mediaRecorderRef.current.start(1000)
      setIsRecording(true)
      setIsPaused(false)
      isRecordingRef.current = true
      isPausedRef.current = false
      isCleaningUpRef.current = false
      setRecordingTime(0)
      setError('')
      setGeneratedNote('')
      setRawAiResponse('')
      setIsTyping(false)

      // Analyze audio for wave animation
      analyzeAudio(stream)
    } catch (error) {
      console.error('Error starting recording:', error)
      setError('Failed to start recording. Please check microphone permissions.')
    }
  }

  // Pause recording function
  const handlePauseRecording = () => {
    if (mediaRecorderRef.current && isRecording && !isPaused) {
      mediaRecorderRef.current.pause()
      setIsPaused(true)
      isPausedRef.current = true
      setAudioLevels([0, 0, 0, 0, 0]) // Clear audio levels when paused
    }
  }

  // Resume recording function
  const handleResumeRecording = () => {
    if (mediaRecorderRef.current && isRecording && isPaused) {
      mediaRecorderRef.current.resume()
      setIsPaused(false)
      isPausedRef.current = false
    }
  }

  // Stop recording function
  const handleStopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop()
      setIsRecording(false)
      setIsPaused(false)
      isRecordingRef.current = false
      isPausedRef.current = false
    }

    // Cleanup audio analysis
    stopAudioAnalysis()
  }

  // Process recording with Whisper + AI
  const processRecording = async (audioBlob) => {
    setIsProcessing(true)
    setIsTyping(false)
    try {
      // Convert blob to file for Whisper
      const audioFile = new File([audioBlob], 'recording.webm', { type: 'audio/webm' })

      console.log('Processing audio:', {
        fileSize: audioFile.size,
        fileType: audioFile.type,
        selectedLanguage: selectedLanguage,
        blobSize: audioBlob.size
      })

      // Step 1: Send to Whisper for transcription with selected language
      const transcript = await whisperCreate(audioFile, selectedLanguage)

      if (!transcript || transcript.trim().length === 0) {
        throw new Error(
          'No speech detected in recording. Please ensure you spoke clearly and your microphone is working properly.'
        )
      }

      console.log('Transcript received:', transcript.substring(0, 100) + '...')

      // Store the original transcript for regeneration
      setOriginalTranscript(transcript)

      // Step 2: Send transcript to AI for clinical formatting in the selected language
      const selectedLanguageName = getLanguageName(selectedLanguage)
      const isEnglish = selectedLanguage === 'en'

      const languageInstruction = isEnglish
        ? `Please format the following English medical transcript into a professional clinical note in English.

Transcript: ${transcript}`
        : `Please format the following medical transcript into a professional clinical note in ${selectedLanguageName}. If the transcript is in a different language, translate it first.

Original transcript: ${transcript}

Output the clinical note in ${selectedLanguageName}.`

      const tmpData = await assistantInitiate(languageInstruction, AssistantEnums.assistant_ids.CLINICAL_NOTES)
      const aiResponse = await generateAIResponse(tmpData)
      const aiContent = aiResponse?.data?.data[0]?.content[0]?.text?.value

      if (!aiContent) {
        throw new Error('Failed to generate clinical note from AI')
      }

      // Store the raw AI response and start typing animation
      setRawAiResponse(aiContent)
      setError('')
      setIsProcessing(false)
      setIsTyping(true)
    } catch (error) {
      console.error('Error processing recording:', error)
      setError(error.message || ERROR_MESSAGES.TRANSCRIPTION_FAILED)
      setIsProcessing(false)
      setIsTyping(false)
    }
  }

  // Regenerate note with specific structure
  const handleRegenerate = async (structureKey) => {
    if (!originalTranscript) {
      console.error('No original transcript available for regeneration')
      return
    }

    setIsProcessing(true)
    setIsTyping(false)
    setError('')

    try {
      // Create prompt with structure requirement in the selected language
      const selectedLanguageName = getLanguageName(selectedLanguage)
      const isEnglish = selectedLanguage === 'en'

      const structurePrompt = isEnglish
        ? `Please format the following English medical transcript into a clinical note using the ${structureKey} structure format in English.

Original transcript: ${originalTranscript}

Please ensure the note follows proper ${structureKey} formatting guidelines and includes all relevant medical information from the transcript.`
        : `Please format the following medical transcript into a clinical note using the ${structureKey} structure format in ${selectedLanguageName}. If the transcript is in a different language, translate it first.

Original transcript: ${originalTranscript}

Please ensure the note follows proper ${structureKey} formatting guidelines and includes all relevant medical information from the transcript. Output the final clinical note in ${selectedLanguageName}.`

      // Send to AI with structure requirements
      const tmpData = await assistantInitiate(structurePrompt, AssistantEnums.assistant_ids.CLINICAL_NOTES)
      const aiResponse = await generateAIResponse(tmpData)
      const aiContent = aiResponse?.data?.data[0]?.content[0]?.text?.value

      if (!aiContent) {
        throw new Error('Failed to regenerate clinical note from AI')
      }

      // Store the new AI response and start typing animation
      setRawAiResponse(aiContent)
      setError('')
      setIsProcessing(false)
      setIsTyping(true)
    } catch (error) {
      console.error('Error regenerating note:', error)
      setError(error.message || 'Failed to regenerate clinical note')
      setIsProcessing(false)
      setIsTyping(false)
    }
  }

  // Handle typing animation completion
  const handleTypingComplete = () => {
    setIsTyping(false)
    setGeneratedNote(rawAiResponse)
  }

  // Handle content changes for editing
  const handleContentChange = (newContent) => {
    setGeneratedNote(newContent)
  }

  // Insert content to note
  const handleInsertToNote = () => {
    if (!generatedNote.trim()) {
      return
    }

    const currentContent = noteState.currentNote || ''
    const newContent = currentContent ? `${currentContent}\n\n${generatedNote}` : generatedNote

    setNoteState((prev) => ({ ...prev, currentNote: newContent, isUpdating: true }))

    // Use debouncedNoteCapture to actually save the note to the database
    if (debouncedNoteCapture && noteState.noteId) {
      debouncedNoteCapture(noteState.noteId, newContent)
    }
  }

  // Save as template (placeholder)
  const handleSaveAsTemplate = () => {
    if (!generatedNote.trim()) {
      return
    }

    // TODO: Implement template saving functionality
  }

  // Retry processing
  const handleRetry = () => {
    if (audioBlob) {
      processRecording(audioBlob)
    }
  }

  // Reset workspace
  const resetWorkspace = async () => {
    setIsRecording(false)
    setIsPaused(false)
    isRecordingRef.current = false
    isPausedRef.current = false
    isCleaningUpRef.current = false
    setRecordingTime(0)
    setAudioBlob(null)
    setIsProcessing(false)
    setIsTyping(false)
    setGeneratedNote('')
    setRawAiResponse('')
    setOriginalTranscript('') // Clear original transcript
    setError('')
    setAudioLevels([0, 0, 0, 0, 0])

    // Clear persisted data
    try {
      await updateWorkspaceData(
        { bookingRef: bookingData._id },
        {
          generatedNote: '',
          rawAiResponse: '',
          originalTranscript: ''
        }
      )
    } catch (error) {
      console.error('Error clearing persisted data:', error)
    }

    if (mediaRecorderRef.current) {
      try {
        mediaRecorderRef.current.stop()
      } catch (e) {
        // MediaRecorder might already be stopped
      }
    }
    clearInterval(timerRef.current)

    // Cleanup audio analysis
    stopAudioAnalysis()
  }

  return {
    // State
    isRecording,
    isPaused,
    recordingTime,
    isProcessing,
    isTyping,
    generatedNote,
    rawAiResponse,
    error,
    audioBlob,
    audioLevels,
    originalTranscript,
    selectedLanguage,

    // Actions
    handleStartRecording,
    handlePauseRecording,
    handleResumeRecording,
    handleStopRecording,
    handleInsertToNote,
    handleSaveAsTemplate,
    handleRetry,
    handleTypingComplete,
    handleContentChange,
    resetWorkspace,
    handleRegenerate,
    setSelectedLanguage,
    clearLanguagePreference
  }
}

export default useSimplifiedTranscribe
